/*
 * WHICH LANGUAGES THE PRODUCT SPEAKS, and the one substitution every dictionary needs.
 *
 * The table lived in the web's `strings.ts`, where it was correct and unreachable: the phone had no
 * i18n at all, and when it grew one the two would each have decided for themselves which codes
 * exist and which of them read right-to-left. That is a disagreement nobody would notice until a
 * Hebrew reader met a screen the other renderer had never been told about — the same class of
 * defect that put `mustBeAsked`, `postVerbs` and `roomToOpen` in here.
 *
 * Only the parts that are TRUE OF THE PRODUCT live here. The strings themselves stay with the
 * renderer that draws them: the web and the phone have different screens, so a shared dictionary
 * would be a single file full of keys that only one of them uses.
 */

export type LangCode = 'en' | 'he' | 'ar';

export interface LangMeta {
  /** The language's own name, shown in a switcher — an endonym, so a speaker recognises their own. */
  readonly label: string;
  /** Writing direction, so a right-to-left language reads correctly wherever it is drawn. */
  readonly dir: 'ltr' | 'rtl';
}

export const LANGS: Record<LangCode, LangMeta> = {
  en: { label: 'English', dir: 'ltr' },
  he: { label: 'עברית', dir: 'rtl' },
  ar: { label: 'العربية', dir: 'rtl' },
};

/** The pickable codes as a typed, ordered list — so a switcher iterates without casting `keyof`. */
export const LANG_ORDER: readonly LangCode[] = ['en', 'he', 'ar'];

/** Whether a stored, guessed or device-supplied value is a language we actually carry. */
export function isLangCode(value: string | null | undefined): value is LangCode {
  return value != null && value in LANGS;
}

/** Substitute `{name}` placeholders with their values. A missing value leaves the token untouched. */
export function fill(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in params ? String(params[key]) : whole,
  );
}
